import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { fetchNotificationUnread } from '@suangua/shared';
import { AuthModal } from '../components/auth/AuthModal';
import { Avatar } from '../components/ui/atoms';
import { useAuth } from '../lib/auth/AuthContext';
import { UIProvider, useUI } from '../lib/ui/UIContext';

/**
 * 用户端布局（原型 web-redesign 同构）：
 * ambient 环境光 + 顶部导航（印章品牌 / 居中导航 / 消息铃铛 / 头像下拉）+ 内容多栏容器 + 页脚 + 顶部 Toast。
 * 注意：AuthModal 必须渲染在 topbar 之外 —— topbar 的 backdrop-filter
 * 会成为 position:fixed 后代的包含块，把模态框限制在 64px 高的头部内。
 *
 * 顶栏右侧（0005 迭代）：
 * - 消息铃铛（仅登录态）：未读角标，点击进 /notifications
 * - 头像改为下拉菜单：个人中心 / 外观切换（原独立按钮移入）/ 退出登录
 */
export function UserLayout() {
  return (
    <UIProvider>
      <div className="ambient" />
      <Shell />
    </UIProvider>
  );
}

function Shell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useUI();
  const { user } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // 导航高亮：/board 与 /board/:id 都归「留言板」；/notifications 也归「我的」
  const p = location.pathname;
  const active = p.startsWith('/board') ? 'board' : p.startsWith('/divination') ? 'divine' : p.startsWith('/me') || p.startsWith('/notifications') ? 'me' : '';

  // 未读消息数：登录后拉取 + 60s 轮询 + 路由切换时刷新（从通知页返回即清零）
  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    let alive = true;
    const tick = () =>
      fetchNotificationUnread()
        .then((r) => {
          if (alive) setUnread(r.count);
        })
        .catch(() => undefined);
    tick();
    const timer = setInterval(tick, 60000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [user, location.pathname]);

  return (
    <div className="flex min-h-screen flex-col">
      {/* ════════ 顶部导航栏 ════════ */}
      <header className="topbar">
        <div className="topbar-inner">
          {/* 品牌区 */}
          <Link to="/" className="brand no-underline">
            <div className="seal font-zh">卦</div>
            <div className="min-w-0">
              <p className="brand-name font-zh">算卦Lite</p>
              <p className="brand-sub">六神煞 · 轻量社区</p>
            </div>
          </Link>

          {/* 主导航（居中） */}
          <nav className="nav-links">
            <NavLink to="/divination" icon="fa-solid fa-wand-magic-sparkles" on={active === 'divine'}>
              占卜
            </NavLink>
            <NavLink to="/board" icon="fa-solid fa-comments" on={active === 'board'}>
              留言板
            </NavLink>
            <NavLink to="/me" icon="fa-solid fa-user" on={active === 'me'}>
              我的
            </NavLink>
          </nav>

          {/* 操作区：消息铃铛 + 用户区 */}
          <div className="topbar-actions">
            {user && (
              <button className="theme-btn bell-btn" onClick={() => navigate('/notifications')} aria-label="消息通知">
                <i className="fa-regular fa-bell" />
                {unread > 0 && <span className="bell-badge">{unread > 99 ? '99+' : unread}</span>}
              </button>
            )}
            <AuthArea onOpenLogin={() => setLoginOpen(true)} />
          </div>
        </div>
      </header>

      {/* 内容多栏容器 */}
      <main className="page">
        <Outlet />
      </main>

      <footer className="site-footer">算卦Lite · 六神煞占卜与轻量社区 · 占卜结果仅供娱乐</footer>

      {/* 登录 / 注册弹层（渲染在 topbar 外，避免 backdrop-filter 包含块问题） */}
      <AuthModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}

/** 顶部导航项（窄屏只留图标，见 index.css @media） */
function NavLink(props: { to: string; icon: string; on: boolean; children: string }) {
  return (
    <Link to={props.to} className={`nav-item${props.on ? ' on' : ''} no-underline`}>
      <i className={props.icon} />
      <span className="nav-label">{props.children}</span>
    </Link>
  );
}

/**
 * 顶部右侧用户区：未登录 → 登录按钮；已登录 → 头像下拉
 * 下拉项：个人中心 / 外观切换（原独立主题按钮移入此处）/ 退出登录。
 */
function AuthArea({ onOpenLogin }: { onOpenLogin: () => void }) {
  const { user, loading, logout } = useAuth();
  const { theme, toggleTheme } = useUI();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // 点击菜单外部关闭
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.avatar-menu-wrap')) setMenuOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  if (loading) {
    return <span className="text-xs text-t3">…</span>;
  }

  if (!user) {
    return (
      <button className="gold-btn-mini" onClick={onOpenLogin}>
        登录 / 注册
      </button>
    );
  }

  return (
    <div className="avatar-menu-wrap">
      <button className="nav-avatar" onClick={() => setMenuOpen((o) => !o)} aria-label="账号菜单">
        <Avatar name={user.username} url={user.avatar_url} size="sm" ring />
      </button>
      {menuOpen && (
        <div className="avatar-menu">
          <button
            className="avatar-menu-item"
            onClick={() => {
              setMenuOpen(false);
              navigate('/me');
            }}
          >
            <i className="fa-solid fa-user w-4 text-center" /> 个人中心
          </button>
          <button
            className="avatar-menu-item"
            onClick={() => {
              toggleTheme();
              setMenuOpen(false);
            }}
          >
            <i className="fa-solid fa-circle-half-stroke w-4 text-center" />
            外观 · {theme === 'dark' ? '宣纸亮色' : '玄墨暗色'}
          </button>
          <button
            className="avatar-menu-item danger"
            onClick={() => {
              setMenuOpen(false);
              void logout();
            }}
          >
            <i className="fa-solid fa-right-from-bracket w-4 text-center" /> 退出登录
          </button>
        </div>
      )}
    </div>
  );
}
